import { MantineProvider } from '@mantine/core';
import _sum from 'lodash/sum';
import type { DataTableColumn } from 'mantine-datatable';
import { DataTable } from 'mantine-datatable';

import type { EstimationModelResponse } from '@/api/schema/data-contracts';
import { FormattedNumber } from '@/components/atoms/FormattedNumber';
import { useMessages } from '@/hooks/messages';
import type { CostEstimationItemsTableRecord, CostModel } from '@/pages/estimation/detail/model';
import { sortCostTypes } from '@/utils/sorting';

import { estimationItemsTableMessageDefinitions } from './messages';

interface Row {
  id: '';
}

export interface SummaryTableProps {
  estimationItems: CostEstimationItemsTableRecord[];
  costs?: CostModel;
  estimationModel: EstimationModelResponse;
  showAssumptions?: boolean;
  hiddenCostTypeColumns?: string[];
}

const SummaryTable: React.FC<SummaryTableProps> = ({
  estimationItems = [],
  estimationModel,
  costs,
  showAssumptions = false,
  hiddenCostTypeColumns = [],
}: SummaryTableProps) => {
  const messages = useMessages(estimationItemsTableMessageDefinitions);

  const costsColumns: DataTableColumn<Row>[] = sortCostTypes(
    estimationModel.costTypes,
    estimationModel.costTypeGroups,
  )
    .filter((costType) => !hiddenCostTypeColumns.includes(costType.id))
    .map((costType) => {
      return {
        accessor: 'costType' + costType.id,
        title: costType.name,
        noWrap: true,
        width: 200,
        textAlign: 'right',
        hidden: !costs,
        titleClassName: 'whitespace-break-spaces',
        render: () => {
          if (!costs) {
            return undefined;
          }

          const calculatedCost = _sum(
            estimationItems
              .filter((estimationItem) => !estimationItem.isOmitted)
              .map((estimationItem) =>
                _sum(
                  estimationItem.estimationItemCosts
                    .filter((itemCost) => itemCost.costTypeId === costType.id)
                    .map((itemCost) => itemCost.price ?? 0),
                ),
              ),
          );
          return <FormattedNumber value={calculatedCost} />;
        },
      };
    });

  const columns: DataTableColumn<Row>[] = [
    {
      accessor: 'hierarchicalOrderReference_placeholder',
      title: '',
      sortable: false,
      width: 80,
      render: () => null,
    },
    {
      accessor: 'name_placeholder',
      title: '',
      sortable: false,
      width: 300,
      render: () => null,
    },
    {
      accessor: 'buttons_placeholder',
      title: '',
      width: 250,
      render: () => null,
    },
    ...(showAssumptions
      ? [
          {
            accessor: 'assumptions_placeholder_1',
            title: '',
            width: 50,
            render: () => null,
          },
        ]
      : []),
    ...costsColumns,

    {
      accessor: 'costSum',
      title: messages.subtotal(),
      noWrap: true,
      width: 200,
      textAlign: 'right',
      titleClassName: 'whitespace-break-spaces',
      render: () => {
        if (!costs) {
          return undefined;
        }

        const calculatedCost = _sum(
          estimationItems
            .filter((estimationItem) => !estimationItem.isOmitted)
            .map((estimationItem) =>
              _sum(estimationItem.estimationItemCosts.map((itemCost) => itemCost.price ?? 0)),
            ),
        );
        return <FormattedNumber value={calculatedCost} />;
      },
    },
    ...(showAssumptions
      ? [
          {
            accessor: 'assumptions_placeholder_2',
            title: '',
            width: 300,
            render: () => null,
          },
        ]
      : []),
  ];

  return (
    <div className='mt-4'>
      <MantineProvider>
        <div className='relative'>
          <div className='pointer-events-none absolute top-0 right-0 z-[5] block h-full w-8 bg-gradient-to-l from-white/80 to-transparent dark:from-black/80' />
          <DataTable<Row>
            highlightOnHover
            withTableBorder
            className='table-hover rounded-xl p-2 whitespace-nowrap'
            records={[
              {
                id: '' /* id is needed just to prevent react warning about missing key property */,
              },
            ]}
            columns={columns}
          />
        </div>
      </MantineProvider>
    </div>
  );
};

export default SummaryTable;
